import { ActionContext, GetterTree, MutationTree } from 'vuex'
import http from '@/http'
import { entityTypePluralize } from './contentHelpers'
import {
  CONTENT_RECEIVED,
  CLEAR_ATTACHMENTS,
  ATTACHMENTS_RECEIVED,
  ATTACHMENTS_UPDATE_LIMIT,
  ATTACHMENTS_UPDATE_OFFSET,
  SET_IS_LOADING_ATTACHMENTS
} from './contentTypes'
import { getRelatedAsMatrix } from './helper'

type State = {
  items: any[],
  offset: number,
  limit: number,
  total: number,
  loading: boolean
}

type Context = ActionContext<State, any>

const state: State = {
  items: [],
  offset: 0,
  limit: 20,
  total: 0,
  loading: false
};

const getters: GetterTree<State, any> = {
  attachmentsMatrix(state: State) {
    return getRelatedAsMatrix(state.items)
  },
  attachmentsAsMatrixMobile(state) {
    return getRelatedAsMatrix(state.items, true)
  },
  attachmentsPage(state: State) {
    return Math.ceil(state.offset + 1)
  },
  hasAllAttachments(state: State) {
    if (!state.total && !state.items.length) {
      return false
    }
    return state.total === state.items.length
  }
}

const actions = {
  async getAttachments({ commit, state, rootGetters, rootState, getters }: Context) {
    if (!rootGetters['user/canViewAttachments']) return
    if (state.loading) return
    if (getters.hasAllAttachments) return

    const entityTypePlural = entityTypePluralize(rootState.content.type)
    const entityId = rootState.content.id
    const url = `${entityTypePlural}/${entityId}/attachments`
    const params = {
      offset: state.offset * state.limit,
      limit: state.limit
    };

    try {
      commit(SET_IS_LOADING_ATTACHMENTS, true)
      const { data, headers } = await http.get(url, { params })
      const items = (state.offset === 0)
        ? data 
        : [...state.items, ...data]
      const total = headers['x-count'] 
        ? Number(headers['x-count'])
        : 0
      commit(ATTACHMENTS_RECEIVED, { items, total })
    } catch (error) {
      commit(ATTACHMENTS_RECEIVED, { items: [] })
      return { error }
    } finally {
      commit(SET_IS_LOADING_ATTACHMENTS, false)
    }
  },

  async getMoreAttachments({ commit, dispatch, getters, state }: Context) {
    if (getters.hasAllAttachments) return
    if (state.loading) return

    const offset = Math.max(getters.attachmentsPage, 0)
    commit(ATTACHMENTS_UPDATE_OFFSET, offset)
    await dispatch('getAttachments')
  },

  async setAttachmentsResultsPerPage({ commit, dispatch }: Context, limit: number) {
    commit(ATTACHMENTS_UPDATE_LIMIT, limit)
    await dispatch('getAttachments')
  },

  clearAttachments({ commit }: Context) {
    commit(CLEAR_ATTACHMENTS)
  }
};

const mutations: MutationTree<State> = {
  [ATTACHMENTS_RECEIVED](state, { items, total }: { items: any[], total: number }) {
    state.items = items
    state.total = total
    state.loading = false
  },
  [CONTENT_RECEIVED](state, { content }) {
    state.items = [];
    state.offset = 0;
    state.limit = 20;
    state.total = content.attachmentsCount || 0;
  },
  [ATTACHMENTS_UPDATE_OFFSET](state, offset: number) {
    state.offset = offset
  },
  [ATTACHMENTS_UPDATE_LIMIT](state, limit: number) {
    state.limit = limit
  },
  [CLEAR_ATTACHMENTS](state) {
    state.items = []
    state.offset = 0
    state.total = 0
    state.loading = false
  },
  [SET_IS_LOADING_ATTACHMENTS](state, loading: boolean) {
    state.loading = loading
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}